import {
  editSmsTemplate,
  deleteSmsTemplate,
  getSmsTemplateData,
  editSubmitSmsTemplate,
  verifySmsTemplate,
  getSmsTemplate
} from 'api/endpoints/SmsTemplate';
import useApi from 'hooks/useApi';
import { debounce, get, isEmpty } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import ReportTable from './ReportTable';
import {
  Button,
  Card,
  CardBody,
  Col,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import FalconCardHeader from 'components/common/FalconCardHeader';
import PropTypes from 'prop-types';
import DynamicForm from 'screens/common/dynamicForm';
import { ConfirmationModal } from 'screens/common/ConfirmationModal';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CardDropdown from 'components/common/CardDropdown';

import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { formSubmissionToast } from 'helpers/utils';
import { getTenantFromToken } from 'utils/routeUtils';

function SmsTemplate({ created }) {
  const tenant = getTenantFromToken();
  const { request: fetchData, data } = useApi(getSmsTemplateData);
  const editSmsTemplateForm = useApi(editSmsTemplate);
  const deleteSmsTemplateAction = useApi(deleteSmsTemplate);
  const verifySmsTemplateAction = useApi(verifySmsTemplate);
  const editSubmitForm = useApi(editSubmitSmsTemplate);
  const getSmsTemplateForm = useApi(getSmsTemplate);
  const [tableData, setTableData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [editRowId, setEditRowId] = useState(null); // Track the ID of the row being edited
  const [isLoading, setIsLoading] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [pageIndex, setPageIndex] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [canNextPage, setCanNextPage] = useState(true);
  const [filters, setFilters] = useState({});
  const [showFilter, setShowFilter] = useState(false);
  const [globalfilters, setGlobalFilters] = useState({
    search_text: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [exportdata, setExportData] = useState(null);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [exportKey, setExportKey] = useState(null);

  // Fetch table data
  const fetchTableData = () => {
    const limit = pageSize;
    const cursorValue = pageIndex * pageSize;
    fetchData({
      cursor: cursorValue,
      limit,
      tenant,
      filter: globalfilters.search_text
    });
  };

  useEffect(() => {
    fetchTableData();
  }, [pageIndex, pageSize, globalfilters, created]);

  // useEffect(() => {
  //   setFetchCallback(fetchTableData);
  // }, [setFetchCallback]);

  useEffect(() => {
    const tabledata = get(data, 'results', []);
    setExportData(tabledata.data);
    setRowCount(get(data, 'results.total', 0));
    setTotalRowCount(get(data, 'results.total', 0));
    setCanNextPage(tabledata?.remaining_records > 0);
    setTableData(tabledata);
    const exportKey = get(data, 'results.export_key', '');
    setExportKey(exportKey);
  }, [data]);

  const handleActionClick = (action, rowData) => {
    const id = rowData?.id;

    if (action === 'edit' && id) {
      setEditRowId(id); // Track the row ID being edited
      setIsLoading(true); // Start loading state
      editSmsTemplateForm.request({ tenant, id });
    } else if (action === 'delete' && id) {
      // Show confirmation dialog before deleting
      setSelectedTemplateId(id);
      setShowDeleteModal(true);
    } else if (action == 'verify' && id) {
      setSelectedTemplateId(id);
      setShowVerifyModal(true);
    }
  };

  useEffect(() => {
    getSmsTemplateForm.request({ tenant });
  }, [tenant]);

  useEffect(() => {
    const formdata = get(getSmsTemplateForm.data, 'results', {});
    const response = get(editSmsTemplateForm.data, 'results', {});
    if (response && !isEmpty(response)) {
      const updatedFormData = { ...formdata };
      updatedFormData.fields = updatedFormData?.fields?.map(field => {
        if (Object.prototype.hasOwnProperty.call(response, field.id)) {
          return {
            ...field,
            defaultValue: response[field.id]
          };
        }
        return field;
      });

      setFormValues(updatedFormData); // Update form values
      setIsLoading(false); // Stop loading state
      setShowModal(true); // Open modal after data is ready
    }
  }, [editSmsTemplateForm.data, getSmsTemplateForm.data]);

  const handleCloseModal = () => {
    setShowModal(false);
    setFormValues({}); // Clear form values when modal is closed
  };

  const handleFormSubmit = async data => {
    const combinedData = { ...data }; // Include row ID and tenant
    try {
      const response = await editSubmitSmsTemplate({
        id: editRowId,
        tenant,
        combinedData
      });
      formSubmissionToast(response, toast, 'Template');
      handleCloseModal();
      fetchTableData();
    } catch (err) {
      formSubmissionToast(err, toast, 'Template');
    }
  };

  const handleDeleteConfirm = async id => {
    if (id) {
      try {
        const res = await deleteSmsTemplate({ tenant, id });
        fetchTableData();
        formSubmissionToast(res, toast, 'Template');
      } catch (err) {
        formSubmissionToast(err, toast, 'Template');
      }
    }
    setShowDeleteModal(false);
  };
  const handleVerify = id => {
    if (id) {
      // Call the delete function
      verifySmsTemplateAction
        .request({ tenant, id })
        .then(() => {
          fetchTableData(); // Refresh table after delete
          toast.success('Template verified successfully'); // Optional alert
        })
        .catch(error => {
          toast.error('Error verifying template');
        });
    }
    setShowVerifyModal(false); // Close modal after confirmation
  };

  const handleClose = () => {
    setShowDeleteModal(false); // Close modal if canceled
  };

  const handleDownload = () => {
    const filename = `SMS_Template.xlsx`; // Excel file name
    const excelData = convertToExcelData(exportdata, filename); // Pass filename to include in title
    downloadExcel(excelData, filename);
  };

  const convertToExcelData = (data, filename) => {
    if (!data || data.length === 0) return [];
    // Generate timestamp
    const now = new Date();
    const timestamp = now
      .toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
      .replace(',', ' at');
    // Create readable title
    const readableName = filename
      .replace('.xlsx', '')
      .replace(/[_-]/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
    const reportTitle = `${readableName} – Generated on ${timestamp} from the FARMSIO Application`;
    // Filter out unwanted keys
    const headers = Object.keys(data[0]).filter(
      key => key !== 'id' && key !== 'action'
    );
    // Map data rows
    const dataRows = data.map(item =>
      headers.map(header => item[header] ?? '')
    );
    // Final Excel structure
    const excelData = [
      [reportTitle], // Title
      [], // Empty row
      headers.map(h =>
        h.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      ), // Formatted headers
      ...dataRows
    ];

    return excelData;
  };

  const downloadExcel = (excelData, filename) => {
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // Optionally merge the title cell across columns
    let merge = [];
    if (excelData.length >= 3 && excelData[2].length > 0) {
      merge = [{ s: { r: 0, c: 0 }, e: { r: 0, c: excelData[2].length - 1 } }];
    }

    ws['!merges'] = merge;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'SMS Template');

    XLSX.writeFile(wb, filename);
  };

  const handleFilterToggle = () => {
    setShowFilter(!showFilter);
  };

  return (
    <div>
      <Row>
        <Col>
          <Card>
            <FalconCardHeader
              title="SMS Template"
              titleTag="h5"
              light={true}
              endEl={
                <div className="d-flex justify-content-end my-auto">
                  <CardDropdown
                    toggleFilters={handleFilterToggle}
                    onExport={handleDownload}
                    exportKey={exportKey}
                  />
                </div>
              }
            />
            <CardBody>
              {tableData && (
                <ReportTable
                  data={tableData?.data}
                  key="Sms_template"
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  pageIndex={pageIndex}
                  setPageIndex={setPageIndex}
                  rowCount={rowCount}
                  setRowCount={setRowCount}
                  totalRowCount={totalRowCount}
                  canNextPage={canNextPage}
                  filters={filters}
                  showFilter={showFilter}
                  setFilters={setFilters}
                  handleAction={handleActionClick} // Handle edit and delete actions
                  setGlobalFilters={setGlobalFilters}
                  setExportData={setExportData}
                />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit SMS Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!isEmpty(formValues) && formValues?.fields && (
            <DynamicForm
              data={formValues}
              modalType="lg"
              onSubmit={handleFormSubmit}
            />
          )}
        </Modal.Body>
        {/* <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer> */}
      </Modal>
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        showModal={showDeleteModal}
        handleClose={handleClose}
        handleConfirm={handleDeleteConfirm}
        templateId={selectedTemplateId}
        actionType="delete"
        actionModel="sms_template"
        title="SMS template"
      />

      <ConfirmationModal
        showModal={showVerifyModal}
        handleClose={() => setShowVerifyModal(false)}
        handleConfirm={handleVerify}
        templateId={selectedTemplateId}
        actionType="verify"
        title="SMS template"
      />
    </div>
  );
}

SmsTemplate.propTypes = {
  handleAction: PropTypes.func,
  setFetchCallback: PropTypes.func,
  created: PropTypes.func
};

export default SmsTemplate;
